import {
  CookieConsent,
  CookieConsentCategory,
  CookieConsentManager,
  DisplayMode,
} from '@almacareer/cookie-consent-manager';
// The package's `exports` map exposes only its script, so the stylesheets are
// reached through the `cookie-consent-css` alias in vite.config.ts.
import 'cookie-consent-css/CookieConsentManager.min.css';
import 'cookie-consent-css/CookieConsentManagerThemes.min.css';

import { updateConsent } from '../consent-mode';
import type { CookieConsentProvider } from '../types';

/** Every UI language has a banner translation; anything else falls back to Croatian. */
const BANNER_LANGUAGES = new Set(['hr', 'sr', 'bs', 'mk']);

let initialized = false;
let consentGiven = false;
const consentListeners = new Set<() => void>();

function isAnalyticsAccepted(): boolean {
  return CookieConsent.acceptedCategory(CookieConsentCategory.ANALYTICS);
}

/** Hands the visitor's choice to GTM, which decides per tag what may run. */
function applyConsent(): void {
  updateConsent(CookieConsent.getUserPreferences().acceptedCategories);
}

export const almaCareerProvider: CookieConsentProvider = {
  init(language: string): void {
    if (initialized) {
      return;
    }

    initialized = true;

    CookieConsentManager('virtualvalley', {
      defaultLang: BANNER_LANGUAGES.has(language) ? language : 'hr',
      // The language follows the fair, not the browser.
      autodetectLang: false,
      // A banner along the bottom; the fair stays usable before a choice.
      displayMode: DisplayMode.SOFT,
      // Consent stays in the visitor's cookie only; nothing is sent to Alma's API.
      // `null` is the documented way to disable it, and the options are spread
      // over the defaults, so it does replace the default URL. The package's
      // type omits `null`.
      consentCollectorApiUrl: null as unknown as string,
      // Runs on every load with saved consent, and right after a first choice.
      onConsent: (): void => {
        applyConsent();
        consentGiven = true;

        for (const listener of consentListeners) {
          listener();
        }

        consentListeners.clear();
      },
      onChange: ({ categories }): void => {
        applyConsent();

        // Scripts GTM already started under a withdrawn category cannot be
        // unloaded, so the page reloads without them. `rejected` lists every rejected category, so only
        // those this change touched count.
        if (categories.changed.some((category) => categories.rejected.includes(category))) {
          window.location.reload();
        }
      },
    });
  },

  showPreferences(): void {
    CookieConsent.showPreferences();
  },

  isAnalyticsConsentGiven(): boolean {
    return initialized && isAnalyticsAccepted();
  },

  onConsentGiven(callback: () => void): () => void {
    if (consentGiven) {
      callback();

      return () => undefined;
    }

    consentListeners.add(callback);

    return (): void => {
      consentListeners.delete(callback);
    };
  },
};
